import React, { useState, useRef, useEffect } from 'react';
import { t } from '../utils/translations';
import { formatCurrency } from '../utils/currency';
import { formatStockWithRetail } from '../utils/uom';
import ProductImage from './ProductImage';
import './SearchableCustomerSelect.css'; // Reuse the same styles

const SearchableProductSelect = ({ products = [], value, onChange, required = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState(products || []);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const productsList = products || [];
    if (searchTerm.trim() === '') {
      setFilteredProducts(productsList);
    } else {
      const filtered = productsList.filter(product =>
        product?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product?.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(product?.barcode || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        product?.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectedProduct = (products || []).find(p => p?.id === value);

  const handleSelect = (productId) => {
    if (onChange) {
      onChange(productId);
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="searchable-select" ref={dropdownRef}>
      <div
        className="select-trigger"
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 0);
          }
        }}
      >
        <span className={selectedProduct ? 'selected-value' : 'placeholder'} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {selectedProduct ? (
            <>
              <ProductImage
                productId={selectedProduct.id}
                hasImage={Boolean(selectedProduct.has_image || selectedProduct.image_filename)}
                alt={selectedProduct.name}
                size={28}
                cacheKey={selectedProduct.updated_at || selectedProduct.image_filename || ''}
              />
              <span>{selectedProduct.name}</span>
            </>
          ) : (
            t('Select Product')
          )}
        </span>
        <span className="arrow">{isOpen ? '▲' : '▼'}</span>
      </div>

      {isOpen && (
        <div className="select-dropdown">
          <div className="search-input-wrapper">
            <input
              ref={inputRef}
              type="text"
              className="search-input"
              placeholder={t('Search products...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="dropdown-options">
            {filteredProducts.length === 0 ? (
              <div className="dropdown-option no-results">
                {t('No products found')}
              </div>
            ) : (
              filteredProducts.map(product => {
                if (!product || !product.id) return null;
                return (
                  <div
                    key={product.id}
                    className={`dropdown-option ${value === product.id ? 'selected' : ''}`}
                    onClick={() => handleSelect(product.id)}
                  >
                    <div className="customer-info" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <ProductImage
                        productId={product.id}
                        hasImage={Boolean(product.has_image || product.image_filename)}
                        alt={product.name}
                        size={40}
                        cacheKey={product.updated_at || product.image_filename || ''}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="customer-name">
                          {product.name || 'N/A'}
                          {product.sku && (
                            <span style={{ color: '#666', fontSize: '12px', marginLeft: '8px' }}>
                              (SKU: {product.sku})
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                          {product.stock_quantity !== undefined && (
                            <div className="customer-detail">
                              {t('Stock')}: {formatStockWithRetail(product)}
                            </div>
                          )}
                          <div className="customer-detail" style={{ fontWeight: '600', color: '#007bff' }}>
                            {formatCurrency(product.price || 0)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableProductSelect;
